import type { PartialDeep } from 'type-fest';
import type { Filter, PropertyType, PropertyTypeFilter } from 'contexts/preferences/listing-params/types';

/** Debounce before parsing while the user types. */
export const PARSE_DEBOUNCE_MS = 350;

/** Debounce before asking the search API how many listings a patch would match. */
export const COUNT_DEBOUNCE_MS = 250;

/** "Any" sentinel for the count-style filters; these never render as a chip. */
export const ANY_COUNT_VALUE = '0+';

/** Every property type off — the base that a parsed subset is spread onto. */
export const EMPTY_HOME_TYPE: PropertyTypeFilter = {
  house: false,
  condo: false,
  townhouse: false,
  farm: false,
  land: false,
  commercial: false,
};

/** Every property type on — the "no property-type preference" state. */
export const ALL_HOME_TYPES: PropertyTypeFilter = {
  house: true,
  condo: true,
  townhouse: true,
  farm: true,
  land: true,
  commercial: true,
};

/**
 * Neutral values for every filter smart search can set. Applied on clear so no
 * chip is left behind, mirroring the chat's reset behavior.
 */
export const RESET_FILTER_PATCH: PartialDeep<Filter> = {
  bedrooms: ANY_COUNT_VALUE,
  bathrooms: ANY_COUNT_VALUE,
  parkingSpaces: ANY_COUNT_VALUE,
  priceMin: null,
  priceMax: null,
  sqftMin: null,
  sqftMax: null,
  listedSince: null,
  listedTo: null,
  homeType: ALL_HOME_TYPES,
  garage: false,
  pool: false,
  fireplace: false,
  waterfront: false,
  openHouse: false,
  status: 'available',
  rental: false,
  additional: {
    condoOrTownhouse: {
      locker: 'any',
      maintenanceFee: null,
    },
  },
  smartTagNames: [],
};

export const HOME_TYPE_LABELS: Record<PropertyType, string> = {
  house: 'House',
  condo: 'Condo',
  townhouse: 'Townhouse',
  farm: 'Farm',
  land: 'Land',
  commercial: 'Commercial',
};

export const STATUS_LABELS: Record<string, string> = {
  available: 'Active',
  'not-available': 'Not available',
  'not-available-sold': 'Sold',
  'not-available-other': 'Off market',
  pending: 'Pending',
  expired: 'Expired',
};

export const SORT_LABELS: Record<string, string> = {
  price: 'Price: low to high',
  '-price': 'Price: high to low',
  date: 'Oldest first',
  '-date': 'Newest first',
  bedrooms: 'Fewest beds',
  '-bedrooms': 'Most beds',
  bathrooms: 'Fewest baths',
  '-bathrooms': 'Most baths',
};

export const LOCKER_LABELS: Record<string, string> = {
  yes: 'Locker',
  no: 'No locker',
  any: 'Locker: any',
};

export const POPULAR_AREAS_SECTION_ID = 'popular-areas';
export const GET_INSPIRED_SECTION_ID = 'get-inspired';
export const MATCHING_LOCATIONS_SECTION_ID = 'matching-locations';
export const MATCHING_LISTINGS_SECTION_ID = 'matching-listings';

/** Debounce before asking Go-Search to complete the place being typed. */
export const LOCATION_SUGGESTION_DEBOUNCE_MS = 250;

/**
 * Shortest place fragment worth completing. Go-Search itself ignores anything
 * shorter, and one or two letters match too much to be a useful list.
 */
export const MIN_PLACE_FRAGMENT_LENGTH = 3;

/** Cap on matching locations, so the list never pushes the card off screen. */
export const MAX_LOCATION_SUGGESTIONS = 5;

export const MAX_LISTING_SUGGESTIONS = 5;

/** Glyph marking a place, on both the suggestion rows and the selected chip. */
export const SMART_SEARCH_PLACE_ICON = '📍';

export const SMART_SEARCH_LISTING_ICON = '🏠';

/**
 * Fewest "Get inspired" chips worth showing. A couple of chips on their own
 * read as a section that failed to load, so a thin set of parsed tags is padded
 * out of the canonical list. A parse that produced more keeps all of them.
 */
export const MIN_INSPIRATION_CHIPS = 5;

export interface SmartSearchInspiration {
  id: string;
  label: string;
  icon: string;
  /** Appended to the query — every entry must be something the parser handles. */
  phrase: string;
}

/**
 * Filter-shaped "Get inspired" chips: each appends a phrase the parser turns
 * into an actual filter.
 *
 * Currently unused. The section is sourced from the canonical tag list instead,
 * which names attributes listings actually carry — once those are filterable a
 * tag chip will narrow the search the way these do, and this list can go.
 */
export const SMART_SEARCH_INSPIRATIONS: readonly SmartSearchInspiration[] = [
  { id: 'beds', label: '3+ beds', icon: '🛏️', phrase: '3 bedrooms' },
  { id: 'baths', label: '2+ baths', icon: '🛁', phrase: '2 bathrooms' },
  { id: 'under-1m', label: 'Under $1M', icon: '💰', phrase: 'under $1m' },
  { id: 'condo', label: 'Condo', icon: '🏢', phrase: 'condo' },
  { id: 'house', label: 'House', icon: '🏡', phrase: 'house' },
  { id: 'townhouse', label: 'Townhouse', icon: '🏘️', phrase: 'townhouse' },
  { id: 'pool', label: 'Pool', icon: '🏊', phrase: 'with a pool' },
  { id: 'garage', label: 'Garage', icon: '🚗', phrase: 'with a garage' },
  { id: 'waterfront', label: 'Waterfront', icon: '🌊', phrase: 'waterfront' },
  { id: 'fireplace', label: 'Fireplace', icon: '🔥', phrase: 'with a fireplace' },
  { id: 'open-house', label: 'Open house', icon: '📅', phrase: 'with an open house' },
  { id: 'new', label: 'New this week', icon: '✨', phrase: 'listed in the last 7 days' },
];
